import React from 'react';
import { Box, ButtonBase, Paper, Typography } from '@mui/material';
import { Bot, Company, Snapshot } from './lib/types';
import { ink, tint } from './lib/tone';
import { hierarchy } from './lib/selectors';
import { fmtIct } from './lib/ict';
import { StatusDot } from './StatusDot';
import { UnreadBadge } from './chips';
import { ACCENT, FONT, GOLD, OVERLINE, RADIUS, SHADOW } from './lib/brand';

const shortTime = (iso?: string | null) => (iso ? fmtIct(iso).replace(' ICT', '') : 'no data');

/** One clickable bot: selects it, filters the Action Center and opens its drawer on Inbox. */
function BotNode({ bot, selected, accent, onSelect, variant, unread = 0 }: { bot: Bot; selected: boolean; accent: { acc: string; soft: string }; onSelect: (b: Bot) => void; variant: 'node' | 'pill'; unread?: number }) {
  const actions = (bot.actions || []).length;
  const isNode = variant === 'node';
  return (
    <ButtonBase
      onClick={() => onSelect(bot)}
      aria-pressed={selected}
      data-testid={`bot-${bot.slug}`}
      sx={{
        display: 'flex', alignItems: isNode ? 'flex-start' : 'center', flexDirection: isNode ? 'column' : 'row', gap: 0.7,
        textAlign: 'left', width: '100%', borderRadius: RADIUS.md, px: 1.1, py: isNode ? 0.9 : 0.55,
        border: '1px solid', borderColor: selected ? accent.acc : 'divider', borderTop: isNode ? '3px solid' : undefined,
        borderTopColor: isNode ? (selected ? accent.acc : 'divider') : undefined,
        borderLeft: selected ? `4px solid ${GOLD}` : undefined,
        bgcolor: selected ? tint(accent.soft) : 'background.paper',
        '&:hover': { boxShadow: SHADOW.hover },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, width: '100%', minWidth: 0 }}>
        <StatusDot color={bot.color} size={isNode ? 10 : 9} />
        <Typography component="span" noWrap sx={{ fontSize: isNode ? 13 : 12.5, fontWeight: 600, flex: 1, minWidth: 0 }}>
          {isNode ? bot.exec_label || bot.short : bot.short}
        </Typography>
        <UnreadBadge count={unread} />
        {!isNode && <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>{actions}</Typography>}
      </Box>
      {isNode && (
        <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
          {actions} action{actions === 1 ? '' : 's'} · {shortTime(bot.last_activity_ict)}
        </Typography>
      )}
    </ButtonBase>
  );
}

export function OrgHierarchy({
  snapshot, company, selected, collapsed, onToggleCluster, onSelect, unread,
}: {
  snapshot: Snapshot;
  company: Company;
  selected: string | null;
  collapsed: Record<string, boolean>;
  onToggleCluster: (name: string) => void;
  onSelect: (b: Bot) => void;
  unread?: Record<string, number>;
}) {
  const h = hierarchy(snapshot, company);
  const accent = ACCENT[company];
  const name = snapshot.companies[company].name;
  return (
    <Paper variant="outlined" component="section" aria-label={`${name} organisation`} data-testid="org-hierarchy" sx={{ p: 2, borderRadius: RADIUS.xl, boxShadow: SHADOW.card }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5, gap: 2, flexWrap: 'wrap' }}>
        <Typography component="h2" sx={{ fontFamily: FONT.heading, fontSize: 15, fontWeight: 700 }}>{name} — organisation</Typography>
        <Typography variant="caption" color="text.secondary">Click any node to open its actions in the Action Center</Typography>
      </Box>

      <Typography variant="overline" color="text.secondary" sx={{ ...OVERLINE, display: 'block', textAlign: 'center', fontSize: 10.5 }}>
        C-suite{company === 'quill' ? ' (LifeScribe execs also lead Quill — TEAM-BRIEF)' : ''}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(6, minmax(0,1fr))' }, gap: 1, mb: 1.5 }}>
        {h.execs.map((b) => (
          <BotNode key={b.slug} bot={b} selected={selected === b.slug} accent={accent} onSelect={onSelect} variant="node" unread={unread?.[b.slug]} />
        ))}
      </Box>

      {h.coordinator && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ ...OVERLINE, display: 'block', textAlign: 'center', fontSize: 10.5 }}>Coordination</Typography>
          <Box sx={{ maxWidth: 520, mx: 'auto', mb: 1.5 }}>
            <ButtonBase
              onClick={() => onSelect(h.coordinator as Bot)}
              aria-pressed={selected === h.coordinator.slug}
              data-testid={`bot-${h.coordinator.slug}`}
              sx={{ width: '100%', textAlign: 'left', display: 'block', border: `2px solid ${accent.acc}`, borderRadius: RADIUS.md, px: 1.4, py: 1, borderLeft: selected === h.coordinator.slug ? `4px solid ${GOLD}` : undefined, bgcolor: selected === h.coordinator.slug ? tint(accent.soft) : 'background.paper' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <StatusDot color={h.coordinator.color} />
                <Typography component="span" sx={{ fontSize: 13, fontWeight: 700 }}>Coordinator</Typography>
                <UnreadBadge count={unread?.[h.coordinator.slug] || 0} />
              </Box>
              <Typography component="div" sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.3 }}>
                {((h.coordinator.doing && h.coordinator.doing.text) || 'No status file found').slice(0, 90)}
              </Typography>
            </ButtonBase>
          </Box>
        </>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: `repeat(${Math.min(Math.max(h.clusters.length, 1), 4)}, minmax(0,1fr))` }, gap: 1.2 }}>
        {h.clusters.map((c) => {
          const isCollapsed = Boolean(collapsed[c.name]);
          const count = (col: string) => c.bots.filter((b) => b.color === col).length;
          return (
            <Box key={c.name} data-testid={`cluster-${c.name}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: RADIUS.lg, p: 1, minWidth: 0 }}>
              <ButtonBase
                onClick={() => onToggleCluster(c.name)}
                aria-expanded={!isCollapsed}
                sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: isCollapsed ? 0 : 0.8, color: ink(accent.ink) }}
              >
                <Typography component="span" sx={{ ...OVERLINE, fontSize: 10.5, textAlign: 'left' }}>
                  {isCollapsed ? '▸' : '▾'} {c.name}
                </Typography>
                <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>{c.bots.length}</Typography>
              </ButtonBase>
              {isCollapsed ? (
                <Typography variant="caption" color="text.secondary">
                  {count('yellow')} pending · {count('green')} active · {count('red')} idle
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {c.bots.map((b) => (
                    <BotNode key={b.slug} bot={b} selected={selected === b.slug} accent={accent} onSelect={onSelect} variant="pill" unread={unread?.[b.slug]} />
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}
